import createClient from 'openapi-fetch';
import { ApiError } from './errors';
import { createAuthMiddleware } from './middleware/auth';
import { createContextMiddleware } from './middleware/context';

/**
 * @typedef {import('./generated/v1').paths} paths
 * @typedef {import('./middleware/context').ContextConfig & import('./middleware/auth').AuthConfig} ContextAndAuth
 * @typedef {ContextAndAuth & {baseUrl: string, fetch?: typeof fetch}} ApiClientConfig
 */

/**
 * A typed v1 client with the context (platform, version, request id) and auth middleware wired.
 * @param {ApiClientConfig} config
 * @returns {import('openapi-fetch').Client<paths>}
 */
export function createApiClient(config) {
  const client = createClient({ baseUrl: config.baseUrl, fetch: config.fetch });
  client.use(createContextMiddleware(config));
  client.use(createAuthMiddleware(config));
  return client;
}

/**
 * Unwraps an openapi-fetch result into the server's envelope (`{ data, meta }`, plus `page` on
 * list routes), or throws an `ApiError` mapped from the `{ error: { code, ... } }` body.
 * @template T
 * @param {Promise<{data?: T, error?: unknown, response: Response}>} request
 * @returns {Promise<T>}
 */
export async function unwrap(request) {
  const { data, error, response } = await request;
  if (error !== undefined || !response.ok) {
    throw ApiError.fromResponseBody(error, response.status);
  }
  return data;
}
